import { ArrowLeftIcon, AirplaneTiltIcon } from '@phosphor-icons/react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useLiveQuery } from 'dexie-react-hooks';

import { EmptyState } from '@/components/empty-state';
import { ICON_WEIGHT } from '@/components/icon';
import { AircraftFlights, AircraftForm } from '@/features/aircraft';
import { aircraft } from '@/lib/db';

/**
 * One aircraft: what it is, and what has been flown in it.
 *
 * The page used to be the edit form alone, which made the aircraft register a
 * place you defined an aeroplane and never looked at again. Its flights belong
 * here — the register is only worth keeping if it answers "what have I flown
 * in this one".
 */
function AircraftDetailPage() {
  const { aircraftId } = Route.useParams();
  const navigate = useNavigate();
  const record = useLiveQuery(() => aircraft.get(aircraftId), [aircraftId], null);

  const backLink = (
    <Link
      to="/aircraft"
      className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 text-sm"
    >
      <ArrowLeftIcon aria-hidden="true" weight={ICON_WEIGHT} className="size-4" />
      Aircraft
    </Link>
  );

  if (record === null) {
    return (
      <div className="mx-auto w-full max-w-xl space-y-4">
        {backLink}
        <p className="text-muted-foreground text-sm">Opening the local database…</p>
      </div>
    );
  }

  if (record === undefined) {
    return (
      <div className="mx-auto w-full max-w-xl space-y-4">
        {backLink}
        <EmptyState
          icon={AirplaneTiltIcon}
          title="Aircraft not found"
          description="Not in the local database. May have been removed on this device."
        />
      </div>
    );
  }

  const typeLine = [record.make, record.model].filter((part) => part !== undefined).join(' ');

  return (
    <div className="mx-auto w-full max-w-3xl space-y-6">
      {backLink}
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h1 className="text-lg font-semibold tracking-tight">{record.registration}</h1>
        {typeLine === '' ? null : <p className="text-muted-foreground text-sm">{typeLine}</p>}
      </div>

      <section className="space-y-3" aria-labelledby="aircraft-flights-heading">
        <h2 id="aircraft-flights-heading" className="text-sm font-semibold tracking-tight">
          Flights in this aircraft
        </h2>
        <AircraftFlights aircraftId={record.id} />
      </section>

      <section className="max-w-xl space-y-3" aria-labelledby="aircraft-details-heading">
        <h2 id="aircraft-details-heading" className="text-sm font-semibold tracking-tight">
          Aircraft details
        </h2>
        <AircraftForm
          initial={record}
          onSaved={() => {
            void navigate({ to: '/aircraft' });
          }}
          onCancel={() => {
            void navigate({ to: '/aircraft' });
          }}
        />
      </section>
    </div>
  );
}

export const Route = createFileRoute('/aircraft/$aircraftId')({
  component: AircraftDetailPage,
});
